"use client"

import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { userSelect, homeScreenStatSelect, allRemoteSettingsSelect } from "../../../redux/selectors/index"
import { notify } from "../../../utility/Helper1"
import { authActions } from "../../../redux/actions"
import WrapUpP2p from "../p2p/layouts/WrapUpP2p"
import PinAuth from "../../../utility/customComponents/PinAuth"
import {
    BanknoteIcon,
    Wallet,
    X,
    Save,
    AlertCircle,
    CheckCircle2,
    Settings,
    Copy,
    Shield,
    ChevronDown,
    ChevronUp,
    RefreshCw,
} from "lucide-react"

// MUI components
import {
    Card,
    CardContent,
    TextField,
    Box,
    Typography,
    Button,
    Slide,
    Snackbar,
    IconButton,
    Fade,
    Paper,
    CircularProgress,
    Backdrop,
    InputAdornment,
    Collapse,
    Grid,
} from "@mui/material"

const PaymentMethod = () => {
    const site_settings = useSelector(allRemoteSettingsSelect)
    const dispatch = useDispatch()
    const userData = useSelector(userSelect)
    const finance = useSelector(homeScreenStatSelect)

    const [securityObj, setSecurityObj] = useState({})
    const [loading, setLoading] = useState(false)
    const [walletAddresses, setWalletAddresses] = useState({})
    const [updatingPaymentDetail, setUpdatingPaymentDetail] = useState(false)
    const [activeTab, setActiveTab] = useState("bank")
    const [notification, setNotification] = useState(null)
    const [bank, setBank] = useState(userData?.bank ?? "")
    const [accountName, setAccountName] = useState(userData?.accountName ?? "")
    const [accountNumber, setAccountNumber] = useState(userData?.accountNumber ?? "")
    const [iban, setIban] = useState(userData?.iban ?? "")
    const [selectedCurrency, setSelectedCurrency] = useState("USD")
    const [copiedAddress, setCopiedAddress] = useState(null)
    const [expandedWallet, setExpandedWallet] = useState(null)

    // Determine which payment methods to show based on autoConfirm
    const showCrypto = site_settings?.plans?.some((el) => el.mother?.autoConfirm == 1)
    const showBank = site_settings?.plans?.some((el) => el.mother?.autoConfirm == 0)

    // Set initial tab based on available payment methods
    useEffect(() => {
        if (showCrypto && !showBank) {
            setActiveTab("wallet")
        } else if (showBank && !showCrypto) {
            setActiveTab("bank")
        }
    }, [showCrypto, showBank])

    useEffect(() => {
        const cryptoWallets = {}
        userData?.bankdetails?.forEach((item) => {
            cryptoWallets[item.name] = item.value
        })
        setWalletAddresses(cryptoWallets)
    }, [userData])

    const updateSpecificCrypto = (cryptoName, value) => {
        setWalletAddresses((prev) => ({
            ...prev,
            [cryptoName]: value,
        }))
    }

    const showNotification = (message, type) => {
        setNotification({ message, type })
        setTimeout(() => setNotification(null), 3000)
    }

    const copyToClipboard = (text, name) => {
        navigator.clipboard.writeText(text)
        setCopiedAddress(name)
        setTimeout(() => setCopiedAddress(null), 2000)
    }

    const doAddressChange = (e) => {
        if (updatingPaymentDetail) return
        e.preventDefault()
        setLoading(true)
        setUpdatingPaymentDetail(true)

        const updateObject = { ...(securityObj ?? {}), ...walletAddresses }

        dispatch(
            authActions.onLogin(`account/account_change/1`, updateObject, (response) => {
                notify(response.msg, response.code)
                setUpdatingPaymentDetail(false)
                setLoading(false)
                if (response.code === 1) {
                    showNotification("Wallet addresses updated successfully", "success")
                } else {
                    showNotification("Failed to update wallet addresses", "error")
                }
            }),
        )
    }

    const doBankUpdate = (e) => {
        if (updatingPaymentDetail) return
        e.preventDefault()
        setLoading(true)
        setUpdatingPaymentDetail(true)

        const updateObject = {
            iban,
            bank,
            accountName,
            accountNumber,
            ...(securityObj ?? {}),
        }

        dispatch(
            authActions.onLogin(`account/account_change/0`, updateObject, (response) => {
                notify(response.msg, response.code)
                setLoading(false)
                setUpdatingPaymentDetail(false)
                if (response.code === 1) {
                    showNotification("Bank details updated successfully", "success")
                } else {
                    showNotification("Failed to update bank details", "error")
                }
            }),
        )
    }

    const hasBankDetails = userData?.bank
    const hasWalletAddresses = userData?.bankdetails?.some((item) => item?.value)

    const toggleWalletExpand = (name) => {
        if (expandedWallet === name) {
            setExpandedWallet(null)
        } else {
            setExpandedWallet(name)
        }
    }

    // If neither payment method should be shown, display a message
    if (!showCrypto && !showBank) {
        return (
            <WrapUpP2p pageTitle="Payment Methods">
                <Box
                    sx={{
                        minHeight: "100vh",
                        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                        color: "white",
                        padding: 4,
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    {/* Neon glow effects */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: "20%",
                            left: "10%",
                            width: "300px",
                            height: "300px",
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(56, 189, 248, 0.3) 0%, rgba(56, 189, 248, 0) 70%)",
                            filter: "blur(60px)",
                            zIndex: 0,
                            animation: "pulse 8s infinite",
                            "@keyframes pulse": {
                                "0%": { opacity: 0.7 },
                                "50%": { opacity: 0.3 },
                                "100%": { opacity: 0.7 },
                            },
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: "10%",
                            right: "15%",
                            width: "250px",
                            height: "250px",
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(232, 121, 249, 0.3) 0%, rgba(232, 121, 249, 0) 70%)",
                            filter: "blur(60px)",
                            zIndex: 0,
                            animation: "pulse2 10s infinite",
                            "@keyframes pulse2": {
                                "0%": { opacity: 0.3 },
                                "50%": { opacity: 0.7 },
                                "100%": { opacity: 0.3 },
                            },
                        }}
                    />

                    <Card
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            background: "rgba(15, 23, 42, 0.7)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            boxShadow: "0 0 40px rgba(56, 189, 248, 0.4), 0 0 20px rgba(232, 121, 249, 0.4) inset",
                            borderRadius: "24px",
                            color: "white",
                            padding: 4,
                            textAlign: "center",
                            maxWidth: "600px",
                            mx: "auto",
                            mt: 8,
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                height: "4px",
                                background: "linear-gradient(90deg, #38bdf8, #818cf8, #e879f9)",
                                animation: "gradient 3s ease infinite",
                                backgroundSize: "200% 200%",
                                "@keyframes gradient": {
                                    "0%": { backgroundPosition: "0% 50%" },
                                    "50%": { backgroundPosition: "100% 50%" },
                                    "100%": { backgroundPosition: "0% 50%" },
                                },
                            }}
                        />
                        <Settings
                            sx={{
                                fontSize: "80px",
                                color: "#38bdf8",
                                mb: 2,
                                filter: "drop-shadow(0 0 15px rgba(56, 189, 248, 0.8))",
                            }}
                        />
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                background: "linear-gradient(90deg, #38bdf8, #818cf8, #e879f9)",
                                backgroundClip: "text",
                                textFillColor: "transparent",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                filter: "drop-shadow(0 0 5px rgba(56, 189, 248, 0.5))",
                            }}
                        >
                            Payment Methods
                        </Typography>
                        <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                            No payment methods available for your selected currency
                        </Typography>
                    </Card>
                </Box>
            </WrapUpP2p>
        )
    }

    return (
        <>
            <WrapUpP2p pageTitle="Payment Methods">
                <Box
                    sx={{
                        minHeight: "100vh",
                        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                        color: "white",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    {/* Enhanced neon glow effects */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: "-150px",
                            left: "-150px",
                            width: "500px",
                            height: "500px",
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(56, 189, 248, 0.3) 0%, rgba(56, 189, 248, 0) 70%)",
                            filter: "blur(80px)",
                            zIndex: 0,
                            animation: "pulse 8s infinite",
                            "@keyframes pulse": {
                                "0%": { opacity: 0.7 },
                                "50%": { opacity: 0.3 },
                                "100%": { opacity: 0.7 },
                            },
                        }}
                    />

                    <Box
                        sx={{
                            position: "absolute",
                            bottom: "-100px",
                            right: "-100px",
                            width: "400px",
                            height: "400px",
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(232, 121, 249, 0.3) 0%, rgba(232, 121, 249, 0) 70%)",
                            filter: "blur(80px)",
                            zIndex: 0,
                            animation: "pulse2 10s infinite",
                            "@keyframes pulse2": {
                                "0%": { opacity: 0.3 },
                                "50%": { opacity: 0.7 },
                                "100%": { opacity: 0.3 },
                            },
                        }}
                    />

                    <Box
                        sx={{
                            position: "absolute",
                            top: "40%",
                            right: "20%",
                            width: "300px",
                            height: "300px",
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(52, 211, 153, 0.2) 0%, rgba(52, 211, 153, 0) 70%)",
                            filter: "blur(60px)",
                            zIndex: 0,
                            animation: "pulse3 12s infinite",
                            "@keyframes pulse3": {
                                "0%": { opacity: 0.5 },
                                "50%": { opacity: 0.2 },
                                "100%": { opacity: 0.5 },
                            },
                        }}
                    />

                    {/* Header */}
                    <Box
                        sx={{
                            padding: 4,
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                mb: 1,
                                background: "linear-gradient(90deg, #38bdf8, #818cf8, #e879f9)",
                                backgroundClip: "text",
                                textFillColor: "transparent",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                filter: "drop-shadow(0 0 10px rgba(56, 189, 248, 0.5))",
                                textShadow: "0 0 20px rgba(56, 189, 248, 0.3)",
                            }}
                        >
                            Payment Methods
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: "rgba(255, 255, 255, 0.8)",
                                maxWidth: "800px",
                                mt: 1,
                                mb: 4,
                                textShadow: "0 0 10px rgba(255, 255, 255, 0.2)",
                            }}
                        >
                            Manage how you receive payments securely on our platform
                        </Typography>

                        {/* Main Content */}
                        <Box
                            sx={{
                                maxWidth: "1200px",
                                mx: "auto",
                                position: "relative",
                                zIndex: 1,
                            }}
                        >
                            {/* Payment Method Tabs - Only show if both payment methods are available */}
                            {showCrypto && showBank && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        mb: 4,
                                        background: "rgba(15, 23, 42, 0.5)",
                                        backdropFilter: "blur(10px)",
                                        borderRadius: "20px",
                                        padding: "4px",
                                        maxWidth: "400px",
                                        border: "1px solid rgba(255, 255, 255, 0.1)",
                                        boxShadow: "0 0 20px rgba(56, 189, 248, 0.3)",
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        onClick={() => setActiveTab("bank")}
                                        sx={{
                                            py: 1.5,
                                            px: 3,
                                            borderRadius: "16px",
                                            background:
                                                activeTab === "bank" ? "linear-gradient(90deg, #38bdf8 0%, #818cf8 100%)" : "transparent",
                                            color: activeTab === "bank" ? "white" : "rgba(255, 255, 255, 0.7)",
                                            fontWeight: 600,
                                            textTransform: "none",
                                            fontSize: "16px",
                                            boxShadow: activeTab === "bank" ? "0 0 15px rgba(56, 189, 248, 0.7)" : "none",
                                            "&:hover": {
                                                background:
                                                    activeTab === "bank"
                                                        ? "linear-gradient(90deg, #38bdf8 0%, #818cf8 100%)"
                                                        : "rgba(255, 255, 255, 0.05)",
                                            },
                                        }}
                                    >
                                        <BanknoteIcon
                                            size={18}
                                            style={{
                                                marginRight: "8px",
                                                filter: activeTab === "bank" ? "drop-shadow(0 0 5px rgba(56, 189, 248, 0.8))" : "none",
                                            }}
                                        />
                                        Bank
                                    </Button>
                                    <Button
                                        fullWidth
                                        onClick={() => setActiveTab("wallet")}
                                        sx={{
                                            py: 1.5,
                                            px: 3,
                                            borderRadius: "16px",
                                            background:
                                                activeTab === "wallet" ? "linear-gradient(90deg, #e879f9 0%, #c026d3 100%)" : "transparent",
                                            color: activeTab === "wallet" ? "white" : "rgba(255, 255, 255, 0.7)",
                                            fontWeight: 600,
                                            textTransform: "none",
                                            fontSize: "16px",
                                            boxShadow: activeTab === "wallet" ? "0 0 15px rgba(232, 121, 249, 0.7)" : "none",
                                            "&:hover": {
                                                background:
                                                    activeTab === "wallet"
                                                        ? "linear-gradient(90deg, #e879f9 0%, #c026d3 100%)"
                                                        : "rgba(255, 255, 255, 0.05)",
                                            },
                                        }}
                                    >
                                        <Wallet
                                            size={18}
                                            style={{
                                                marginRight: "8px",
                                                filter: activeTab === "wallet" ? "drop-shadow(0 0 5px rgba(232, 121, 249, 0.8))" : "none",
                                            }}
                                        />
                                        Crypto
                                    </Button>
                                </Box>
                            )}

                            {/* Bank Accounts Tab */}
                            {showBank && activeTab === "bank" && (
                                <Fade in={true} timeout={500}>
                                    <Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mb: 3,
                                            }}
                                        >
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: "#38bdf8",
                                                    textShadow: "0 0 10px rgba(56, 189, 248, 0.5)",
                                                }}
                                            >
                                                Bank Details
                                            </Typography>
                                        </Box>

                                        <Card
                                            sx={{
                                                background: "rgba(15, 23, 42, 0.7)",
                                                backdropFilter: "blur(10px)",
                                                borderRadius: "24px",
                                                border: "1px solid rgba(56, 189, 248, 0.3)",
                                                boxShadow: "0 0 30px rgba(56, 189, 248, 0.3), 0 0 10px rgba(56, 189, 248, 0.1) inset",
                                                overflow: "hidden",
                                                mb: 4,
                                                position: "relative",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: "3px",
                                                    background: "linear-gradient(90deg, #38bdf8, #818cf8)",
                                                    animation: "gradient 3s ease infinite",
                                                    backgroundSize: "200% 200%",
                                                    "@keyframes gradient": {
                                                        "0%": { backgroundPosition: "0% 50%" },
                                                        "50%": { backgroundPosition: "100% 50%" },
                                                        "100%": { backgroundPosition: "0% 50%" },
                                                    },
                                                }}
                                            />

                                            <CardContent sx={{ p: 0 }}>
                                                <Box
                                                    sx={{
                                                        p: 3,
                                                        background:
                                                            "linear-gradient(90deg, rgba(56, 189, 248, 0.15) 0%, rgba(129, 140, 248, 0.15) 100%)",
                                                        borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 3,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 60,
                                                            height: 60,
                                                            borderRadius: "20px",
                                                            background: "rgba(56, 189, 248, 0.2)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            boxShadow: "0 0 15px rgba(56, 189, 248, 0.4)",
                                                        }}
                                                    >
                                                        <BanknoteIcon
                                                            size={30}
                                                            style={{ color: "#38bdf8", filter: "drop-shadow(0 0 5px rgba(56, 189, 248, 0.8))" }}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                color: "white",
                                                                fontWeight: 600,
                                                                textShadow: "0 0 10px rgba(255, 255, 255, 0.3)",
                                                            }}
                                                        >
                                                            {hasBankDetails ? "Your Bank Account" : "Add Bank Account"}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                                                            {hasBankDetails
                                                                ? "Edit your bank details below"
                                                                : "Enter your bank details to receive payments"}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ p: 3 }}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={6}>
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        color: "rgba(255, 255, 255, 0.8)",
                                                                        fontWeight: 500,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            display: "inline-block",
                                                                            width: "8px",
                                                                            height: "8px",
                                                                            borderRadius: "50%",
                                                                            background: "#38bdf8",
                                                                            marginRight: "8px",
                                                                            boxShadow: "0 0 8px #38bdf8",
                                                                        }}
                                                                    ></span>
                                                                    Bank Name
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    placeholder="Enter bank name"
                                                                    value={bank}
                                                                    onChange={(e) => setBank(e.target.value)}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-root": {
                                                                            color: "white",
                                                                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                                                                            borderRadius: "12px",
                                                                            "& fieldset": {
                                                                                borderColor: "rgba(56, 189, 248, 0.3)",
                                                                                borderWidth: "1px",
                                                                            },
                                                                            "&:hover fieldset": {
                                                                                borderColor: "rgba(56, 189, 248, 0.5)",
                                                                            },
                                                                            "&.Mui-focused fieldset": {
                                                                                borderColor: "#38bdf8",
                                                                                borderWidth: "1px",
                                                                            },
                                                                            "&.Mui-focused": {
                                                                                boxShadow: "0 0 0 2px rgba(56, 189, 248, 0.3)",
                                                                            },
                                                                        },
                                                                        "& .MuiInputLabel-root": {
                                                                            color: "rgba(255, 255, 255, 0.7)",
                                                                        },
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        color: "rgba(255, 255, 255, 0.8)",
                                                                        fontWeight: 500,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            display: "inline-block",
                                                                            width: "8px",
                                                                            height: "8px",
                                                                            borderRadius: "50%",
                                                                            background: "#38bdf8",
                                                                            marginRight: "8px",
                                                                            boxShadow: "0 0 8px #38bdf8",
                                                                        }}
                                                                    ></span>
                                                                    Account Name
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    placeholder="Enter account name"
                                                                    value={accountName}
                                                                    onChange={(e) => setAccountName(e.target.value)}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-root": {
                                                                            color: "white",
                                                                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                                                                            borderRadius: "12px",
                                                                            "& fieldset": {
                                                                                borderColor: "rgba(56, 189, 248, 0.3)",
                                                                                borderWidth: "1px",
                                                                            },
                                                                            "&:hover fieldset": {
                                                                                borderColor: "rgba(56, 189, 248, 0.5)",
                                                                            },
                                                                            "&.Mui-focused fieldset": {
                                                                                borderColor: "#38bdf8",
                                                                                borderWidth: "1px",
                                                                            },
                                                                            "&.Mui-focused": {
                                                                                boxShadow: "0 0 0 2px rgba(56, 189, 248, 0.3)",
                                                                            },
                                                                        },
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        color: "rgba(255, 255, 255, 0.8)",
                                                                        fontWeight: 500,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            display: "inline-block",
                                                                            width: "8px",
                                                                            height: "8px",
                                                                            borderRadius: "50%",
                                                                            background: "#38bdf8",
                                                                            marginRight: "8px",
                                                                            boxShadow: "0 0 8px #38bdf8",
                                                                        }}
                                                                    ></span>
                                                                    Account Number
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    placeholder="Enter account number"
                                                                    value={accountNumber}
                                                                    onChange={(e) => setAccountNumber(e.target.value)}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-root": {
                                                                            color: "white",
                                                                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                                                                            borderRadius: "12px",
                                                                            "& fieldset": {
                                                                                borderColor: "rgba(56, 189, 248, 0.3)",
                                                                                borderWidth: "1px",
                                                                            },
                                                                            "&:hover fieldset": {
                                                                                borderColor: "rgba(56, 189, 248, 0.5)",
                                                                            },
                                                                            "&.Mui-focused fieldset": {
                                                                                borderColor: "#38bdf8",
                                                                                borderWidth: "1px",
                                                                            },
                                                                            "&.Mui-focused": {
                                                                                boxShadow: "0 0 0 2px rgba(56, 189, 248, 0.3)",
                                                                            },
                                                                        },
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Grid>

                                                        {1 > 2 && <Grid item xs={12} md={6}>
                                                            <Box sx={{ mb: 3 }}>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        mb: 0.5,
                                                                        color: "rgba(255, 255, 255, 0.8)",
                                                                        fontWeight: 500,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            display: "inline-block",
                                                                            width: "8px",
                                                                            height: "8px",
                                                                            borderRadius: "50%",
                                                                            background: "#38bdf8",
                                                                            marginRight: "8px",
                                                                            boxShadow: "0 0 8px #38bdf8",
                                                                        }}
                                                                    ></span>
                                                                    IBAN or SWIFT Code
                                                                </Typography>
                                                                <TextField
                                                                    fullWidth
                                                                    placeholder="Enter IBAN or SWIFT code"
                                                                    value={iban}
                                                                    onChange={(e) => setIban(e.target.value)}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-root": {
                                                                            color: "white",
                                                                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                                                                            borderRadius: "12px",
                                                                            "& fieldset": {
                                                                                borderColor: "rgba(56, 189, 248, 0.3)",
                                                                                borderWidth: "1px",
                                                                            },
                                                                            "&:hover fieldset": {
                                                                                borderColor: "rgba(56, 189, 248, 0.5)",
                                                                            },
                                                                            "&.Mui-focused fieldset": {
                                                                                borderColor: "#38bdf8",
                                                                                borderWidth: "1px",
                                                                            },
                                                                            "&.Mui-focused": {
                                                                                boxShadow: "0 0 0 2px rgba(56, 189, 248, 0.3)",
                                                                            },
                                                                        },
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Grid>}

                                                    </Grid>


                                                    <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />

                                                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                                                        <Button
                                                            startIcon={
                                                                updatingPaymentDetail ? (
                                                                    <CircularProgress size={16} color="inherit" />
                                                                ) : (
                                                                    <Save size={18} />
                                                                )
                                                            }
                                                            onClick={doBankUpdate}
                                                            disabled={updatingPaymentDetail}
                                                            sx={{
                                                                py: 1.5,
                                                                px: 4,
                                                                borderRadius: "12px",
                                                                background: "linear-gradient(90deg, #38bdf8 0%, #818cf8 100%)",
                                                                color: "white",
                                                                textTransform: "none",
                                                                fontWeight: 600,
                                                                boxShadow: "0 0 20px rgba(56, 189, 248, 0.5)",
                                                                "&:hover": {
                                                                    background: "linear-gradient(90deg, #38bdf8 0%, #818cf8 100%)",
                                                                    boxShadow: "0 0 30px rgba(56, 189, 248, 0.7)",
                                                                },
                                                                "&.Mui-disabled": {
                                                                    background: "rgba(56, 189, 248, 0.3)",
                                                                    color: "rgba(255, 255, 255, 0.5)",
                                                                },
                                                                position: "relative",
                                                                overflow: "hidden",
                                                                "&::before": {
                                                                    content: '""',
                                                                    position: "absolute",
                                                                    top: 0,
                                                                    left: "-100%",
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    background:
                                                                        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                                                                    transition: "0.5s",
                                                                },
                                                                "&:hover::before": {
                                                                    left: "100%",
                                                                },
                                                            }}
                                                        >
                                                            {updatingPaymentDetail ? "Saving..." : "Save Bank Details"}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </Fade>
                            )}

                            {/* Crypto Wallets Tab */}
                            {showCrypto && activeTab === "wallet" && (
                                <Fade in={true} timeout={500}>
                                    <Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mb: 3,
                                            }}
                                        >
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: "#e879f9",
                                                    textShadow: "0 0 10px rgba(232, 121, 249, 0.5)",
                                                }}
                                            >
                                                Crypto Wallets
                                            </Typography>
                                        </Box>

                                        <Card
                                            sx={{
                                                background: "rgba(15, 23, 42, 0.7)",
                                                backdropFilter: "blur(10px)",
                                                borderRadius: "24px",
                                                border: "1px solid rgba(232, 121, 249, 0.3)",
                                                boxShadow: "0 0 30px rgba(232, 121, 249, 0.3), 0 0 10px rgba(232, 121, 249, 0.1) inset",
                                                overflow: "hidden",
                                                mb: 4,
                                                position: "relative",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: "3px",
                                                    background: "linear-gradient(90deg, #e879f9, #c026d3)",
                                                    animation: "gradient 3s ease infinite",
                                                    backgroundSize: "200% 200%",
                                                    "@keyframes gradient": {
                                                        "0%": { backgroundPosition: "0% 50%" },
                                                        "50%": { backgroundPosition: "100% 50%" },
                                                        "100%": { backgroundPosition: "0% 50%" },
                                                    },
                                                }}
                                            />

                                            <CardContent sx={{ p: 0 }}>
                                                <Box
                                                    sx={{
                                                        p: 3,
                                                        background:
                                                            "linear-gradient(90deg, rgba(232, 121, 249, 0.15) 0%, rgba(192, 38, 211, 0.15) 100%)",
                                                        borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 3,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 60,
                                                            height: 60,
                                                            borderRadius: "20px",
                                                            background: "rgba(232, 121, 249, 0.2)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            boxShadow: "0 0 15px rgba(232, 121, 249, 0.4)",
                                                        }}
                                                    >
                                                        <Wallet
                                                            size={30}
                                                            style={{ color: "#e879f9", filter: "drop-shadow(0 0 5px rgba(232, 121, 249, 0.8))" }}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                color: "white",
                                                                fontWeight: 600,
                                                                textShadow: "0 0 10px rgba(255, 255, 255, 0.3)",
                                                            }}
                                                        >
                                                            {hasWalletAddresses ? "Your Crypto Wallets" : "Add Crypto Wallets"}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                                                            {hasWalletAddresses
                                                                ? "Edit your wallet addresses below"
                                                                : "Enter your wallet addresses to receive crypto payments"}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ p: 3 }}>
                                                    <Grid container spacing={3}>
                                                        {userData?.bankdetails?.map((item, index) => (
                                                            <Grid item xs={12} md={6} key={index}>
                                                                <Box
                                                                    sx={{
                                                                        mb: 3,
                                                                        p: 2,
                                                                        borderRadius: "16px",
                                                                        background: "rgba(232, 121, 249, 0.05)",
                                                                        border: "1px solid rgba(232, 121, 249, 0.2)",
                                                                        boxShadow: "0 0 15px rgba(232, 121, 249, 0.1) inset",
                                                                        transition: "all 0.3s ease",
                                                                        "&:hover": {
                                                                            boxShadow: "0 0 20px rgba(232, 121, 249, 0.2) inset",
                                                                        },
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                                                                        <Box
                                                                            sx={{
                                                                                width: 40,
                                                                                height: 40,
                                                                                borderRadius: "12px",
                                                                                background: "rgba(232, 121, 249, 0.1)",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                boxShadow: "0 0 10px rgba(232, 121, 249, 0.3)",
                                                                                overflow: "hidden",
                                                                            }}
                                                                        >
                                                                            {finance?.find((finItem) => finItem.type === item.type)?.image ? (
                                                                                <img
                                                                                    src={
                                                                                        finance?.find((finItem) => finItem.type === item.type)?.image ||
                                                                                        "/placeholder.svg"
                                                                                    }
                                                                                    alt={item.name}
                                                                                    style={{ width: "24px", height: "24px" }}
                                                                                />
                                                                            ) : (
                                                                                <Wallet
                                                                                    size={20}
                                                                                    style={{
                                                                                        color: "#e879f9",
                                                                                        filter: "drop-shadow(0 0 5px rgba(232, 121, 249, 0.8))",
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                        <Box sx={{ flex: 1 }}>
                                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                                <Typography
                                                                                    variant="subtitle2"
                                                                                    sx={{
                                                                                        color: "white",
                                                                                        fontWeight: 600,
                                                                                        textShadow: "0 0 5px rgba(255, 255, 255, 0.3)",
                                                                                    }}
                                                                                >
                                                                                    {item.name}
                                                                                </Typography>
                                                                                <Box
                                                                                    sx={{
                                                                                        py: 0.5,
                                                                                        px: 1,
                                                                                        borderRadius: "6px",
                                                                                        background: "rgba(232, 121, 249, 0.1)",
                                                                                        border: "1px solid rgba(232, 121, 249, 0.2)",
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: "#e879f9",
                                                                                            fontWeight: 600,
                                                                                            textShadow: "0 0 5px rgba(232, 121, 249, 0.5)",
                                                                                        }}
                                                                                    >
                                                                                        {item.abbr}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Box>
                                                                        </Box>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => toggleWalletExpand(item.name)}
                                                                            sx={{
                                                                                color: "rgba(255, 255, 255, 0.7)",
                                                                                "&:hover": { color: "#e879f9" },
                                                                            }}
                                                                        >
                                                                            {expandedWallet === item.name ? (
                                                                                <ChevronUp size={18} />
                                                                            ) : (
                                                                                <ChevronDown size={18} />
                                                                            )}
                                                                        </IconButton>
                                                                    </Box>

                                                                    <TextField
                                                                        fullWidth
                                                                        placeholder={`Enter ${item.name} wallet address`}
                                                                        value={walletAddresses[item.name] || ""}
                                                                        onChange={(e) => updateSpecificCrypto(item.name, e.target.value)}
                                                                        variant="outlined"
                                                                        InputProps={{
                                                                            endAdornment: walletAddresses[item.name] ? (
                                                                                <InputAdornment position="end">
                                                                                    <IconButton
                                                                                        edge="end"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation()
                                                                                            copyToClipboard(walletAddresses[item.name], item.name)
                                                                                        }}
                                                                                        sx={{
                                                                                            color:
                                                                                                copiedAddress === item.name ? "#e879f9" : "rgba(255, 255, 255, 0.5)",
                                                                                            "&:hover": { color: "#e879f9" },
                                                                                        }}
                                                                                    >
                                                                                        {copiedAddress === item.name ? (
                                                                                            <CheckCircle2 size={18} />
                                                                                        ) : (
                                                                                            <Copy size={18} />
                                                                                        )}
                                                                                    </IconButton>
                                                                                </InputAdornment>
                                                                            ) : null,
                                                                        }}
                                                                        sx={{
                                                                            "& .MuiOutlinedInput-root": {
                                                                                color: "white",
                                                                                backgroundColor: "rgba(255, 255, 255, 0.05)",
                                                                                borderRadius: "12px",
                                                                                "& fieldset": {
                                                                                    borderColor: "rgba(232, 121, 249, 0.3)",
                                                                                    borderWidth: "1px",
                                                                                },
                                                                                "&:hover fieldset": {
                                                                                    borderColor: "rgba(232, 121, 249, 0.5)",
                                                                                },
                                                                                "&.Mui-focused fieldset": {
                                                                                    borderColor: "#e879f9",
                                                                                    borderWidth: "1px",
                                                                                },
                                                                                "&.Mui-focused": {
                                                                                    boxShadow: "0 0 0 2px rgba(232, 121, 249, 0.3)",
                                                                                },
                                                                            },
                                                                        }}
                                                                    />

                                                                    <Collapse in={expandedWallet === item.name}>
                                                                        <Box sx={{ mt: 2 }}>
                                                                            <Box
                                                                                sx={{
                                                                                    py: 2,
                                                                                    px: 3,
                                                                                    borderRadius: "12px",
                                                                                    background: "rgba(232, 121, 249, 0.1)",
                                                                                    border: "1px solid rgba(232, 121, 249, 0.2)",
                                                                                    fontFamily: "monospace",
                                                                                    wordBreak: "break-all",
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    alignItems: "center",
                                                                                }}
                                                                            >
                                                                                <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)" }}>
                                                                                    {walletAddresses[item.name] || "No address set"}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Collapse>
                                                                </Box>
                                                            </Grid>
                                                        ))}
                                                    </Grid>

                                                    <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />

                                                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                                                        <Button
                                                            startIcon={
                                                                updatingPaymentDetail ? (
                                                                    <CircularProgress size={16} color="inherit" />
                                                                ) : (
                                                                    <Save size={18} />
                                                                )
                                                            }
                                                            onClick={doAddressChange}
                                                            disabled={updatingPaymentDetail}
                                                            sx={{
                                                                py: 1.5,
                                                                px: 4,
                                                                borderRadius: "12px",
                                                                background: "linear-gradient(90deg, #e879f9 0%, #c026d3 100%)",
                                                                color: "white",
                                                                textTransform: "none",
                                                                fontWeight: 600,
                                                                boxShadow: "0 0 20px rgba(232, 121, 249, 0.5)",
                                                                "&:hover": {
                                                                    background: "linear-gradient(90deg, #e879f9 0%, #c026d3 100%)",
                                                                    boxShadow: "0 0 30px rgba(232, 121, 249, 0.7)",
                                                                },
                                                                "&.Mui-disabled": {
                                                                    background: "rgba(232, 121, 249, 0.3)",
                                                                    color: "rgba(255, 255, 255, 0.5)",
                                                                },
                                                                position: "relative",
                                                                overflow: "hidden",
                                                                "&::before": {
                                                                    content: '""',
                                                                    position: "absolute",
                                                                    top: 0,
                                                                    left: "-100%",
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    background:
                                                                        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                                                                    transition: "0.5s",
                                                                },
                                                                "&:hover::before": {
                                                                    left: "100%",
                                                                },
                                                            }}
                                                        >
                                                            {updatingPaymentDetail ? "Saving..." : "Save Wallet Addresses"}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </Fade>
                            )}
                        </Box>
                    </Box>
                </Box>
            </WrapUpP2p>


            {/* Loading Backdrop */}
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backdropFilter: "blur(8px)",
                    background: "rgba(15, 23, 42, 0.8)",
                }}
                open={loading}
            >
                <Box sx={{ textAlign: "center" }}>
                    <Box
                        sx={{
                            position: "relative",
                            width: 80,
                            height: 80,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress
                            color="inherit"
                            size={80}
                            sx={{
                                color: "#38bdf8",
                                filter: "drop-shadow(0 0 15px rgba(56, 189, 248, 0.8))",
                            }}
                        />
                        <RefreshCw
                            size={30}
                            style={{
                                position: "absolute",
                                color: "white",
                                filter: "drop-shadow(0 0 5px rgba(255, 255, 255, 0.8))",
                            }}
                        />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            mt: 3,
                            fontWeight: 600,
                            color: "white",
                            textShadow: "0 0 10px rgba(255, 255, 255, 0.5)",
                        }}
                    >
                        Processing...
                    </Typography>
                </Box>
            </Backdrop>
        </>
    )
}

export default PaymentMethod
